import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface DesktopIcon {
  id: string;
  label: string;
  iconComponent: React.ReactNode;
  onClick: () => void;
  position: { x: number; y: number };
}

interface DesktopContextType {
  icons: DesktopIcon[];
  selectedIcons: string[];
  setSelectedIcons: (ids: string[]) => void;
  addIcon: (icon: Omit<DesktopIcon, "position">, position?: { x: number; y: number }) => void;
  removeIcon: (id: string) => void;
  updateIconPosition: (id: string, x: number, y: number) => void;
  getIcon: (id: string) => DesktopIcon | undefined;
}

const DesktopContext = createContext<DesktopContextType | undefined>(undefined);

export function useDesktop() {
  const context = useContext(DesktopContext);
  if (!context) {
    throw new Error("useDesktop must be used within a DesktopProvider");
  }
  return context;
}

export default function DesktopProvider({ children }: { children: ReactNode }) {
  const [icons, setIcons] = useState<DesktopIcon[]>([]);
  const [selectedIcons, setSelectedIcons] = useState<string[]>([]);

  const addIcon = useCallback(
    (icon: Omit<DesktopIcon, "position">, position?: { x: number; y: number }) => {
      // calculate default position based on existing icons
      const defaultPosition = position || {
        x: 20,
        y: 20 + icons.length * 100,
      };

      setIcons((prev) => [
        ...prev,
        {
          ...icon,
          position: defaultPosition,
        },
      ]);
    },
    [icons.length],
  );

  const removeIcon = useCallback((id: string) => {
    setIcons((prev) => prev.filter((icon) => icon.id !== id));
    setSelectedIcons((prev) => prev.filter((iconId) => iconId !== id));
  }, []);

  const updateIconPosition = useCallback((id: string, x: number, y: number) => {
    setIcons((prev) =>
      prev.map((icon) => (icon.id === id ? { ...icon, position: { x, y } } : icon)),
    );
  }, []);

  const getIcon = useCallback(
    (id: string) => {
      return icons.find((icon) => icon.id === id);
    },
    [icons],
  );

  const value: DesktopContextType = {
    icons,
    selectedIcons,
    setSelectedIcons,
    addIcon,
    removeIcon,
    updateIconPosition,
    getIcon,
  };

  return <DesktopContext.Provider value={value}>{children}</DesktopContext.Provider>;
}
